import {
    alpha,
    createTheme,
    darken,
    getContrastRatio,
    lighten,
} from '@mui/material';

enum Colors {
    Coral = '#E76F51',
    Denim = '#14374A',
    Fossil = '#787878',
    Granola = '#F4B44F',
    Sea = '#4EB0AD',
    Red = '#D0342C',
    Sapphire = '#5cb5b3',
    Shadow = '#3d3d3d',
    White = '#fcfcfc',
    Black = '#333333',
    Owner = '#FA8072',
    OnBehalf = '#ffff99',
    Completed = '#CCCCCC',
    Pending = '#ffffff',
    Property = '#3b6978',
    Boat = '#A7C7E7',
    Activity = '#93C572',
    Vehicle = '#006400',
    Commercial = '#4B0082',
    CalendarDefault = '#748e81',
    Homeworld = '#0d98e3',
    SevenDay = '#375565',
    Flight = '#95D0CE',
    Transfer = '#F5BC61',
    Restaurant = '#EC8C74',
    Event = '#B95941',
    Other = '#8A9BA5',
    ServiceTypeCleaning = '#4EB0AD',
    ServiceTypeCatering = '#EC8C74',
    ServiceTypeAlcohol = '#8E4585',
    ServiceTypeWorkshop = '#5B7FA5',
    ServiceTypeWellness = '#6BA368',
    ServiceTypeOther = '#8A9BA5',
    MapDrivingRoute = '#4285F4',
    MapFlight = '#1565c0',
    LiveLocationGreen = '#22c55e',
    LiveLocationGreenDark = '#16a34a',
    FormBackground = '#EFF3F6',
    FormFieldBorder = '#BECED7',
}

enum Fonts {
    Inter = '"Inter", sans-serif',
    Merriweather = '"Merriweather", serif',
    Montserrat = '"Montserrat", sans-serif',
}

declare module '@mui/material/styles' {
    interface Palette {
        dark: {
            main: string;
            contrastText: string;
        };
        neutral: {
            main: string;
            contrastText: string;
        };
        green: {
            main: string;
            contrastText: string;
        };
        white: {
            main: string;
            contrastText: string;
        };
        yellow: {
            main: string;
            contrastText: string;
        };
        lightGrey: {
            main: string;
            contrastText: string;
        };
        midGrey: {
            main: string;
            contrastText: string;
        };
        gray: {
            main: string;
            contrastText: string;
        };
        red: {
            main: string;
            contrastText: string;
        };
        blue: {
            main: string;
            contrastText: string;
        };
        black: {
            main: string;
            contrastText: string;
        };
        highlight: {
            main: string;
            contrastText: string;
        };
        textAccent: {
            main: string;
            contrastText: string;
        };
        faded: {
            main: string;
            contrastText: string;
        };
        calendar: {
            owner: string;
            onBehalf: string;
            completed: string;
            pending: string;
            property: string;
            boat: string;
            activity: string;
            vehicle: string;
            commercial: string;
            calendarDefault: string;
        };
        itinerary: {
            sevenDay: string;
            flight: string;
            transfer: string;
            restaurant: string;
            event: string;
            other: string;
        };
        serviceType: {
            cleaning: string;
            catering: string;
            alcohol: string;
            workshop: string;
            wellness: string;
            other: string;
        };

        itineraryMap: {
            drivingRoute: string;
            flight: string;
        };
        liveLocation: {
            main: string;
            dark: string;
        };
        form: {
            background: string;
            fieldBorder: string;
        };
        chatComponent: {
            userBackground: string;
            assistantBackground: string;
            userText: string;
            assistantText: string;
            sendButton: string;
        };
        policy: {
            flexible: string;
            fair: string;
            firm: string;
            nonRefundable: string;
        };
        custom: {
            columnHeader: string;
            columnBorder: string;
            cardActionBackground: string;
            modalActionBackground: string;
            modalActionBorder: string;
            grey: string;
            lightGrey: string;
            lighterGrey: string;
            textHighlight: string;
            sapphire: string;
            coral: string;
            profileAvatar: string;
            toolbar: string;
            bodyTextColor: string;
            bodyBackgroundColor: string;
            button: {
                borderRadius: string;
                primary: {
                    textColor: string;
                    backgroundColor: string;
                    hoverBackgroundColor: string;
                };
                secondary: {
                    textColor: string;
                    backgroundColor: string;
                    hoverBackgroundColor: string;
                };
                tab: {
                    textColor: string;
                    backgroundColor: string;
                    hoverTextColor: string;
                    hoverBackgroundColor: string;
                };
                mid: {
                    textColor: string;
                    backgroundColor: string;
                    hoverBackgroundColor: string;
                };
                warning: {
                    textColor: string;
                    backgroundColor: string;
                    hoverBackgroundColor: string;
                };
            };
            sidebar: {
                badgeColor: string;
                badgeBackgroundColor: string;
                closeIconColor: string;
                closeIconBackgroundColor: string;
                closeIconHoverColor: string;
                closeIconHoverBackgroundColor: string;
                iconTextColor: string;
                iconBackgroundColor: string;
                menuItemColor: string;
                menuItemActiveColor: string;
                menuItemHoverColor: string;
                menuItemHoverBackgroundColor: string;
            };
            lightTextColor: string;
            lightBorderColor: string;
            iconFillColor: string;
            lightHighlightColor: string;
            lightHoverBackground: string;
            darkHoverBackground: string;
            logo: string;
            lightLogo: string;
            loggedOutBackgroundImage: string;
        };
    }
    interface PaletteOptions {
        dark?: {
            main?: string;
            contrastText?: string;
        };
        neutral?: {
            main?: string;
            contrastText?: string;
        };
        green?: {
            main?: string;
            contrastText?: string;
        };
        other?: {
            main?: string;
            contrastText?: string;
        };
        white?: {
            main?: string;
            contrastText?: string;
        };
        yellow?: {
            main?: string;
            contrastText?: string;
        };
        lightGrey?: {
            main?: string;
            contrastText?: string;
        };
        midGrey?: {
            main?: string;
            contrastText?: string;
        };
        gray?: {
            main?: string;
            contrastText?: string;
        };
        red?: {
            main?: string;
            contrastText?: string;
        };
        blue?: {
            main?: string;
            contrastText?: string;
        };
        black?: {
            main?: string;
            contrastText?: string;
        };
        highlight?: {
            main?: string;
            contrastText?: string;
        };
        textAccent?: {
            main?: string;
            contrastText?: string;
        };
        faded?: {
            main?: string;
            contrastText?: string;
        };
        calendar?: {
            owner?: string;
            onBehalf?: string;
            completed?: string;
            pending?: string;
            property?: string;
            boat?: string;
            activity?: string;
            vehicle?: string;
            commercial?: string;
            calendarDefault?: string;
        };
        itinerary?: {
            sevenDay?: string;
            flight?: string;
            transfer?: string;
            restaurant?: string;
            event?: string;
            other?: string;
        };
        serviceType?: {
            cleaning?: string;
            catering?: string;
            alcohol?: string;
            workshop?: string;
            wellness?: string;
            other?: string;
        };
        itineraryMap?: {
            drivingRoute?: string;
            flight?: string;
        };
        liveLocation?: {
            main?: string;
            dark?: string;
        };
        form?: {
            background?: string;
            fieldBorder?: string;
        };
        chatComponent?: {
            userBackground?: string;
            assistantBackground?: string;
            userText?: string;
            assistantText?: string;
            sendButton?: string;
        };
        policy?: {
            flexible?: string;
            fair?: string;
            firm?: string;
            nonRefundable?: string;
        };
        custom?: {
            columnHeader?: string;
            columnBorder?: string;
            cardActionBackground?: string;
            modalActionBackground?: string;
            modalActionBorder?: string;
            grey?: string;
            lightGrey?: string;
            lighterGrey?: string;
            textHighlight?: string;
            sapphire?: string;
            coral?: string;
            profileAvatar?: string;
            toolbar?: string;
            bodyTextColor?: string;
            bodyBackgroundColor?: string;
            button?: {
                borderRadius?: string;
                primary?: {
                    textColor?: string;
                    backgroundColor?: string;
                    hoverBackgroundColor?: string;
                };
                secondary?: {
                    textColor?: string;
                    backgroundColor?: string;
                    hoverBackgroundColor?: string;
                };
                tab?: {
                    textColor?: string;
                    backgroundColor?: string;
                    hoverTextColor?: string;
                    hoverBackgroundColor?: string;
                };
                mid?: {
                    textColor?: string;
                    backgroundColor?: string;
                    hoverBackgroundColor?: string;
                };
                warning?: {
                    textColor?: string;
                    backgroundColor?: string;
                    hoverBackgroundColor?: string;
                };
            };
            sidebar?: {
                badgeColor?: string;
                badgeBackgroundColor?: string;
                closeIconColor?: string;
                closeIconBackgroundColor?: string;
                closeIconHoverColor?: string;
                closeIconHoverBackgroundColor?: string;
                iconTextColor?: string;
                iconBackgroundColor?: string;
                menuItemColor?: string;
                menuItemActiveColor?: string;
                menuItemHoverColor?: string;
                menuItemHoverBackgroundColor?: string;
            };
            lightTextColor?: string;
            lightBorderColor?: string;
            iconFillColor?: string;
            lightHoverBackground?: string;
            darkHoverBackground?: string;
            logo?: string;
            lightLogo?: string;
            loggedOutBackgroundImage?: string;
        };
    }

    interface TypographyVariants {
        subHeading: React.CSSProperties;
        visuallyHidden: React.CSSProperties;
        body3: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        subHeading?: React.CSSProperties;
        visuallyHidden?: React.CSSProperties;
        body3?: React.CSSProperties;
    }
}
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        subHeading: true;
        visuallyHidden: true;
        body3: true;
    }
}

declare module '@mui/material/Paper' {
    interface PaperPropsVariantOverrides {
        nudge: true;
    }
}

const themeSettings = {
    classic: {
        dark: {
            main: Colors.Denim,
            contrastText: Colors.White,
        },
        neutral: {
            main: Colors.White,
            contrastText: Colors.Denim,
        },
        green: {
            main: Colors.Sea,
            contrastText: Colors.Denim,
        },
        white: {
            main: Colors.White,
            contrastText: Colors.Denim,
        },
        yellow: {
            main: darken(Colors.Granola, 0.05),
            contrastText: Colors.Denim,
        },
        lightGrey: {
            main: darken(Colors.White, 0.1),
            contrastText: Colors.Denim,
        },
        midGrey: {
            main: lighten(Colors.Denim, 0.6),
            contrastText: Colors.Denim,
        },
        gray: {
            main: darken(Colors.White, 0.7),
            contrastText: Colors.White,
        },
        red: {
            main: Colors.Red,
            contrastText: Colors.Denim,
        },
        blue: {
            main: Colors.Denim,
            contrastText: Colors.Sea,
        },
        black: {
            main: Colors.Black,
            contrastText: Colors.White,
        },
        warning: {
            main: Colors.Red,
            light: '#FAEEEE',
            contrastText: Colors.White,
        },
        primary: {
            main: lighten(Colors.Denim, 0.1),
            light: lighten(Colors.Denim, 0.6),
            dark: Colors.Denim,
            contrastText: lighten(Colors.Denim, 0.99),
        },
        secondary: {
            main: Colors.Granola,
            contrastText: Colors.Denim,
            dark: darken(Colors.Granola, 0.05),
        },
        info: {
            main: darken(Colors.Sea, 0.09),
            contrastText: Colors.Denim,
        },
        text: {
            primary: lighten(Colors.Denim, 0.15),
            secondary: lighten(Colors.Denim, 0.32),
        },
        highlight: {
            main: darken(Colors.Granola, 0.25),
            contrastText: Colors.White,
        },
        textAccent: {
            main: darken(Colors.Sea, 0.3),
            contrastText: lighten(Colors.Sea, 0.9),
        },
        faded: {
            main: darken(Colors.White, 0.07),
            contrastText: Colors.Denim,
        },
        background: Colors.White,
        calendar: {
            owner: Colors.Owner,
            onBehalf: Colors.OnBehalf,
            completed: Colors.Completed,
            pending: Colors.Pending,
            property: Colors.Property,
            boat: Colors.Boat,
            activity: Colors.Activity,
            vehicle: Colors.Vehicle,
            commercial: Colors.Commercial,
            calendarDefault: Colors.CalendarDefault,
        },
        itinerary: {
            sevenDay: Colors.SevenDay,
            flight: Colors.Flight,
            transfer: Colors.Transfer,
            restaurant: Colors.Restaurant,
            event: Colors.Event,
            other: Colors.Other,
        },
        serviceType: {
            cleaning: Colors.ServiceTypeCleaning,
            catering: Colors.ServiceTypeCatering,
            alcohol: Colors.ServiceTypeAlcohol,
            workshop: Colors.ServiceTypeWorkshop,
            wellness: Colors.ServiceTypeWellness,
            other: Colors.ServiceTypeOther,
        },
        itineraryMap: {
            drivingRoute: Colors.MapDrivingRoute,
            flight: Colors.MapFlight,
        },
        liveLocation: {
            main: Colors.LiveLocationGreen,
            dark: Colors.LiveLocationGreenDark,
        },
        form: {
            background: Colors.FormBackground,
            fieldBorder: Colors.FormFieldBorder,
        },

        policy: {
            flexible: Colors.Sea,
            fair: darken(Colors.Granola, 0.05),
            firm: Colors.Homeworld,
            nonRefundable: Colors.Red,
        },
        typography: {
            fontFamily: Fonts.Inter,
            body1: {
                fontFamily: Fonts.Inter,
                fontSize: '1rem',
                color: lighten(Colors.Denim, 0.15),
                fontWeight: '400',
            },
            body2: {
                fontFamily: Fonts.Inter,
                fontSize: '.875rem',
                color: lighten(Colors.Denim, 0.15),
                fontWeight: '400',
            },
            body3: {
                fontFamily: Fonts.Inter,
                fontSize: '.75rem',
                color: lighten(Colors.Denim, 0.15),
                fontWeight: '400',
            },
            quote: Fonts.Merriweather,
            subHeading: {
                fontFamily: Fonts.Montserrat,
                fontSize: '1.3rem',
            },
            h1: {
                fontFamily: Fonts.Montserrat,
                fontSize: '1.8rem',
                fontWeight: '600',
                textDecoration: 'underline',
                textDecorationColor: Colors.Granola,
                textUnderlineOffset: '8px',
            },
            h2: {
                fontFamily: Fonts.Montserrat,
                fontSize: '1.4rem',
                fontWeight: '600',
            },
            h3: {
                fontFamily: Fonts.Montserrat,
                fontSize: '1rem',
                fontWeight: '700',
            },
            h4: {
                fontFamily: Fonts.Montserrat,
                fontSize: '1.1rem',
                fontWeight: '600',
            },
            h5: {
                fontFamily: Fonts.Montserrat,
                fontSize: '1.1rem',
                fontWeight: '600',
            },
            h6: {
                fontFamily: Fonts.Montserrat,
                fontSize: '.8rem',
                fontWeight: '600',
            },
            caption: {
                fontFamily: Fonts.Inter,
                fontSize: '.875rem',
                fontWeight: '700',
            },
        },
        custom: {
            themeColor: Colors.Denim,
            bodyTextColor: lighten(Colors.Denim, 0.15),
            bodyBackgroundColor: Colors.White,
            sapphire: Colors.Sapphire,
            coral: Colors.Coral,
            button: {
                borderRadius: '4px',
                primary: {
                    textColor: Colors.White,
                    backgroundColor: Colors.Denim,
                    hoverBackgroundColor: darken(Colors.Denim, 0.1),
                },
                secondary: {
                    textColor: Colors.Denim,
                    backgroundColor: Colors.Granola,
                    hoverBackgroundColor: darken(Colors.Granola, 0.05),
                },
                tab: {
                    textColor: Colors.Denim,
                    backgroundColor: 'transparent',
                    hoverTextColor: Colors.Denim,
                    hoverBackgroundColor: Colors.Sea,
                },
                mid: {
                    textColor: Colors.Fossil,
                    backgroundColor: Colors.Fossil,
                    hoverBackgroundColor: darken(Colors.Fossil, 0.1),
                },
                warning: {
                    textColor: Colors.White,
                    backgroundColor: Colors.Red,
                    hoverBackgroundColor: darken(Colors.Red, 0.1),
                },
            },
            sidebar: {
                badgeColor: Colors.Denim,
                badgeBackgroundColor: Colors.Granola,
                closeIconColor: Colors.White,
                closeIconBackgroundColor: lighten(Colors.Denim, 0.1),
                closeIconHoverColor: Colors.White,
                closeIconHoverBackgroundColor: darken(Colors.Granola, 0.2),
                iconTextColor: Colors.Denim,
                iconBackgroundColor: Colors.Granola,
                menuItemColor: Colors.White,
                menuItemActiveColor: Colors.Granola,
                menuItemHoverColor: Colors.White,
                menuItemHoverBackgroundColor: lighten(Colors.Denim, 0.1),
            },
            lightBorderColor: darken(Colors.White, 0.1),
            lightTextColor: Colors.White,
            iconFillColor: Colors.White,
            lightHoverBackground: lighten(Colors.Denim, 0.3),
            darkHoverBackground: lighten(Colors.Denim, 0.7),
            logo: '/logos/LogoDark.png',
            lightLogo: '/logos/LogoLight.png',
            loggedOutBackgroundImage: `linear-gradient(rgba(20,55,74,0.3), rgba(20,55,74,0.3)), url('/illustrations/loginBackground.png') no-repeat center bottom`,
        },
    },
};

enum Themes {
    Classic = 'classic',
}

export const myTheme = themeSettings[Themes.Classic]; // Switch from Legacy to Classic to use new theme

export const theme = createTheme({
    palette: {
        dark: {
            main: myTheme.dark.main,
            contrastText: myTheme.dark.contrastText,
        },
        neutral: {
            main: myTheme.neutral.main,
            contrastText: myTheme.neutral.contrastText,
        },
        green: {
            main: myTheme.green.main,
            contrastText: myTheme.green.contrastText,
        },
        yellow: {
            main: myTheme.yellow.main,
            contrastText: myTheme.yellow.contrastText,
        },
        lightGrey: {
            main: myTheme.lightGrey.main,
            contrastText: myTheme.lightGrey.contrastText,
        },
        midGrey: {
            main: myTheme.midGrey.main,
            contrastText: myTheme.midGrey.contrastText,
        },
        gray: {
            main: myTheme.gray.main,
            contrastText: myTheme.gray.contrastText,
        },
        red: {
            main: myTheme.red.main,
            contrastText: myTheme.red.contrastText,
        },
        blue: {
            main: myTheme.blue.main,
            contrastText: myTheme.blue.contrastText,
        },
        black: {
            main: myTheme.black.main,
            contrastText: myTheme.black.contrastText,
        },
        warning: {
            main: myTheme.warning.main,
            light: myTheme.warning.light,
            contrastText: myTheme.warning.contrastText,
        },
        highlight: {
            main: myTheme.highlight.main,
            contrastText: myTheme.highlight.contrastText,
        },
        textAccent: {
            main: myTheme.textAccent.main,
            contrastText: myTheme.textAccent.contrastText,
        },
        faded: {
            main: myTheme.faded.main,
            contrastText: myTheme.textAccent.contrastText,
        },
        background: { default: myTheme.background },
        mode: 'light',
        primary: {
            main: myTheme.primary.main,
            light: myTheme.primary.light,
            dark: myTheme.primary.dark,
            contrastText: myTheme.primary.contrastText,
        },
        secondary: {
            main: myTheme.secondary.main,
            contrastText: myTheme.secondary.contrastText,
            dark: myTheme.secondary.dark,
        },
        error: {
            main: '#e42d31',
            dark: '#b0171a',
            contrastText: '#fff',
        },
        success: {
            main: '#21884a',
            contrastText: '#fff',
        },
        info: {
            main: myTheme.info.main,
            contrastText: myTheme.info.contrastText,
        },
        text: {
            primary: myTheme.text.primary,
            secondary: myTheme.text.secondary,
        },
        calendar: {
            owner: myTheme.calendar.owner,
            onBehalf: myTheme.calendar.onBehalf,
            completed: myTheme.calendar.completed,
            pending: myTheme.calendar.pending,
            property: myTheme.calendar.property,
            boat: myTheme.calendar.boat,
            activity: myTheme.calendar.activity,
            vehicle: myTheme.calendar.vehicle,
            commercial: myTheme.calendar.commercial,
            calendarDefault: myTheme.calendar.calendarDefault,
        },
        itinerary: {
            sevenDay: myTheme.itinerary.sevenDay,
            flight: myTheme.itinerary.flight,
            transfer: myTheme.itinerary.transfer,
            restaurant: myTheme.itinerary.restaurant,
            event: myTheme.itinerary.event,
            other: myTheme.itinerary.other,
        },
        serviceType: {
            cleaning: myTheme.serviceType.cleaning,
            catering: myTheme.serviceType.catering,
            alcohol: myTheme.serviceType.alcohol,
            workshop: myTheme.serviceType.workshop,
            wellness: myTheme.serviceType.wellness,
            other: myTheme.serviceType.other,
        },
        itineraryMap: {
            drivingRoute: myTheme.itineraryMap.drivingRoute,
            flight: myTheme.itineraryMap.flight,
        },
        liveLocation: {
            main: myTheme.liveLocation.main,
            dark: myTheme.liveLocation.dark,
        },
        form: {
            background: myTheme.form.background,
            fieldBorder: myTheme.form.fieldBorder,
        },
        chatComponent: {
            userBackground: lighten(myTheme.green.main, 0.1),
            assistantBackground: myTheme.lightGrey.main,
            userText: myTheme.white.main,
            assistantText: myTheme.text.primary,
            sendButton: myTheme.secondary.main,
        },
        policy: {
            flexible: myTheme.policy.flexible,
            fair: myTheme.policy.fair,
            firm: myTheme.policy.firm,
            nonRefundable: myTheme.policy.nonRefundable,
        },
        custom: {
            columnHeader: '#000',
            columnBorder: myTheme.midGrey.main,
            cardActionBackground: '#f6f8f9',
            modalActionBackground: '#eeeeee',
            modalActionBorder: '#d8d8d8',
            grey: '#E0E0E0',
            lightGrey: '#F5F5F5',
            lighterGrey: '#FAFAFA',
            textHighlight: '#ffeb3b',
            sapphire: myTheme.custom.sapphire,
            coral: myTheme.custom.coral,
            profileAvatar:
                getContrastRatio(myTheme.primary.main, myTheme.secondary.main) >
                2.9
                    ? myTheme.secondary.main
                    : myTheme.secondary.contrastText,
            toolbar: myTheme.custom.themeColor,
            bodyTextColor: myTheme.custom.bodyTextColor,
            button: {
                borderRadius: myTheme.custom.button.borderRadius,
                primary: {
                    textColor: myTheme.custom.button.primary.textColor,
                    backgroundColor:
                        myTheme.custom.button.primary.backgroundColor,
                    hoverBackgroundColor:
                        myTheme.custom.button.primary.hoverBackgroundColor,
                },
                secondary: {
                    textColor: myTheme.custom.button.secondary.textColor,
                    backgroundColor:
                        myTheme.custom.button.secondary.backgroundColor,
                    hoverBackgroundColor:
                        myTheme.custom.button.secondary.hoverBackgroundColor,
                },
                tab: {
                    textColor: myTheme.custom.button.tab.textColor,
                    backgroundColor: 'transparent',
                    hoverTextColor: myTheme.custom.button.tab.hoverTextColor,
                    hoverBackgroundColor:
                        myTheme.custom.button.tab.hoverBackgroundColor,
                },
                mid: {
                    textColor: myTheme.custom.button.mid.textColor,
                    backgroundColor: myTheme.custom.button.mid.backgroundColor,
                    hoverBackgroundColor:
                        myTheme.custom.button.mid.hoverBackgroundColor,
                },
                warning: {
                    textColor: myTheme.custom.button.warning.textColor,
                    backgroundColor:
                        myTheme.custom.button.warning.backgroundColor,
                    hoverBackgroundColor:
                        myTheme.custom.button.warning.hoverBackgroundColor,
                },
            },
            sidebar: {
                badgeColor: myTheme.custom.sidebar.badgeColor,
                badgeBackgroundColor:
                    myTheme.custom.sidebar.badgeBackgroundColor,
                closeIconColor: myTheme.custom.sidebar.closeIconColor,
                closeIconBackgroundColor:
                    myTheme.custom.sidebar.closeIconBackgroundColor,
                closeIconHoverColor: myTheme.custom.sidebar.closeIconHoverColor,
                closeIconHoverBackgroundColor:
                    myTheme.custom.sidebar.closeIconHoverBackgroundColor,
                iconTextColor: myTheme.custom.sidebar.iconTextColor,
                iconBackgroundColor: myTheme.custom.sidebar.iconBackgroundColor,
                menuItemColor: myTheme.custom.sidebar.menuItemColor,
                menuItemActiveColor: myTheme.custom.sidebar.menuItemActiveColor,
                menuItemHoverColor: myTheme.custom.sidebar.menuItemHoverColor,
                menuItemHoverBackgroundColor:
                    myTheme.custom.sidebar.menuItemHoverBackgroundColor,
            },
            lightBorderColor: myTheme.custom.lightBorderColor,
            lightTextColor: myTheme.custom.lightTextColor,
            iconFillColor: myTheme.custom.iconFillColor,
            lightHoverBackground: myTheme.custom.lightHoverBackground,
            darkHoverBackground: myTheme.custom.darkHoverBackground,
            logo: myTheme.custom.logo,
            lightLogo: myTheme.custom.lightLogo,
            loggedOutBackgroundImage: myTheme.custom.loggedOutBackgroundImage,
        },
    },
    typography: {
        fontFamily: myTheme.typography.fontFamily,
        // allVariants: {
        //     fontFamily: myTheme.typography.fontFamily,
        // },
        body1: myTheme.typography.body1,
        body2: myTheme.typography.body2,
        body3: myTheme.typography.body3,
        subHeading: myTheme.typography.subHeading,
        visuallyHidden: {
            clip: 'rect(0, 0, 0, 0)',
            height: '1px',
            overflow: 'hidden',
            position: 'absolute',
            whiteSpace: 'nowrap',
            width: '1px',
        },
        h1: myTheme.typography.h1,
        h2: myTheme.typography.h2,
        h3: myTheme.typography.h3,
        h4: myTheme.typography.h4,
        h5: myTheme.typography.h5,
        h6: myTheme.typography.h6,
        caption: myTheme.typography.caption,
    },
    components: {
        MuiAutocomplete: {
            styleOverrides: {
                inputRoot: {
                    '&.Mui-disabled': {
                        backgroundColor: '#eef1f4',
                        opacity: 1,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: Colors.FormFieldBorder,
                        },
                    },
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: myTheme.info.main,
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableRipple: true,
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    fontFamily: myTheme.typography.fontFamily,
                    textTransform: 'none',
                    fontWeight: '600',
                    borderColor: alpha(myTheme.text.primary, 0.4),
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                label: {
                    fontFamily: myTheme.typography.fontFamily,
                },
            },
        },
        MuiDialogContent: {
            defaultProps: {
                dividers: true,
            },
            styleOverrides: {
                root: {
                    backgroundColor: myTheme.form.background,
                    borderTop: 'none',
                    borderBottom: 'none',
                    color: myTheme.text.primary,
                    '& h2': {
                        marginTop: 0,
                        marginBottom: 2,
                    },
                    '& hr, & .MuiDivider-root': {
                        color: myTheme.text.secondary,
                        borderColor: myTheme.text.secondary,
                    },
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    borderTop: `1px solid ${myTheme.custom.lightBorderColor}`,
                    backgroundColor: myTheme.neutral.main,
                },
            },
        },
        MuiDialogTitle: {
            defaultProps: {
                component: 'h1',
                variant: 'h3',
            },
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${myTheme.custom.lightBorderColor}`,
                    backgroundColor: myTheme.neutral.main,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                },
            },
        },
        MuiIconButton: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...myTheme.typography.body2,
                    marginBottom: 4,
                    color: myTheme.text.primary,
                    fontWeight: '500',
                },
                asterisk: {
                    color: myTheme.warning.main,
                },
            },
        },
        MuiListItemText: {
            defaultProps: {
                primaryTypographyProps: {
                    color: 'text.primary',
                },
                secondaryTypographyProps: {
                    color: 'text.primary',
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                square: false,
            },
            styleOverrides: {
                rounded: {
                    borderRadius: '8px',
                },
                elevation1: {
                    boxShadow:
                        'rgba(65, 74, 82, 0.2) 0px 4px 8px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
                },
            },
            variants: [
                {
                    props: { square: true },
                    style: {
                        borderRadius: 0,
                    },
                },
                {
                    props: { variant: 'nudge' },
                    style: ({ theme }) => ({
                        boxShadow: theme.shadows[6],
                        border: `1px solid ${myTheme.yellow.main}`,
                        color: darken(myTheme.yellow.main, 0.35),
                        backgroundColor: lighten(myTheme.yellow.main, 0.78),
                        maxWidth: '100%',
                        marginInline: 'auto',
                        padding: theme.spacing(1.25, 1.25),
                        [theme.breakpoints.up('sm')]: {
                            padding: theme.spacing(1.25, 2),
                        },
                        [theme.breakpoints.up('md')]: {
                            maxWidth: 980,
                        },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: theme.spacing(1.5),
                        flexWrap: 'wrap',
                        pointerEvents: 'auto',
                        position: 'relative',
                    }),
                },
            ],
        },
        MuiPopover: {
            defaultProps: {
                disableScrollLock: true,
            },
            styleOverrides: {
                paper: {
                    boxShadow:
                        'rgba(149, 157, 165, 0.2) 0px 4px 8px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    '&.Mui-disabled': {
                        backgroundColor: '#eef1f4',
                        color: Colors.Fossil,
                        WebkitTextFillColor: Colors.Fossil,
                        opacity: 1,
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    color: myTheme.text.primary,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiInputBase-root:not(.Mui-disabled)': {
                        color: myTheme.text.primary,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: Colors.White,
                    // Default MUI focus uses 2px outline — reads as a thick “inner” double line
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 1,
                    },
                    '&.Mui-disabled': {
                        opacity: 1,
                        backgroundColor: '#eef1f4',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: Colors.FormFieldBorder,
                        },
                    },
                },
                input: {
                    '&.Mui-disabled': {
                        color: Colors.Fossil,
                        WebkitTextFillColor: Colors.Fossil,
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    fontSize: '14px',
                    color: myTheme.text.primary,
                    '&.Mui-disabled': {
                        backgroundColor: 'transparent',
                        color: Colors.Fossil,
                        WebkitTextFillColor: Colors.Fossil,
                    },
                    '::placeholder': {
                        opacity: 1,
                        color: myTheme.text.secondary,
                    },
                },
            },
            defaultProps: {
                sx: {
                    borderRadius: 2,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontFamily: myTheme.typography.body1.fontFamily,
                    padding: '20px',
                    backgroundColor: myTheme.neutral.main,
                    color: myTheme.text.primary,
                    border: `1px solid ${lighten(myTheme.text.primary, 0.8)}`,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: myTheme.text.primary,
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    color: myTheme.text.secondary,
                    fontSize: myTheme.typography.body3.fontSize,
                    fontWeight: 400,
                    '&.Mui-error': {
                        fontWeight: 400,
                    },
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: darken(myTheme.green.main, 0.3),
                    transition: 'color 0.2s ease',
                    '&:hover': {
                        color: myTheme.text.primary,
                        cursor: 'pointer',
                    },
                },
            },
        },
    },
});
